import {
  unitSchema,
  type BuildingDefinition,
  type Faction,
  type SectorDefinition,
  type UnitDefinition,
} from './types';

type RawUnit = Omit<UnitDefinition, 'id' | 'faction' | 'source'> & {
  id: string;
  faction: Faction;
  source: string;
};
const unit = (raw: RawUnit): UnitDefinition => unitSchema.parse(raw);

const eq = (
  id: string,
  name: string,
  source: string,
  category: UnitDefinition['category'],
  cost: number,
  requestTime: number,
  capacity: number,
  role: string,
  options: Partial<UnitDefinition> = {},
): UnitDefinition =>
  unit({
    id,
    faction: 'equestria',
    name,
    source,
    category,
    cost,
    requestTime,
    capacity,
    health: options.health ?? (category === 'infantry' ? 180 : category === 'air' ? 440 : 550),
    speed: options.speed ?? (category === 'infantry' ? 72 : category === 'air' ? 180 : 95),
    range: options.range ?? 210,
    damage: options.damage ?? 18,
    armor: options.armor ?? 0,
    members: options.members,
    air: options.air,
    antiAir: options.antiAir,
    role,
    abilities: options.abilities ?? [],
  });
const idf = (
  id: string,
  name: string,
  source: string,
  category: UnitDefinition['category'],
  cost: number,
  requestTime: number,
  capacity: number,
  role: string,
  options: Partial<UnitDefinition> = {},
): UnitDefinition =>
  unit({
    id,
    faction: 'interdef',
    name,
    source,
    category,
    cost,
    requestTime,
    capacity,
    health: options.health ?? (category === 'infantry' ? 180 : category === 'air' ? 440 : 560),
    speed: options.speed ?? (category === 'infantry' ? 72 : category === 'air' ? 180 : 95),
    range: options.range ?? 210,
    damage: options.damage ?? 18,
    armor: options.armor ?? 0,
    members: options.members,
    air: options.air,
    antiAir: options.antiAir,
    role,
    abilities: options.abilities ?? [],
  });

export const units: UnitDefinition[] = [
  eq(
    'eq_rifle_section',
    'Equestrian Rifle Section',
    'eq_training_center',
    'infantry',
    240,
    18,
    4,
    'Capture and line infantry',
    { members: 6, abilities: ['Grenade'], range: 180 },
  ),
  eq(
    'eq_combat_engineers',
    'Combat Engineer Section',
    'eq_training_center',
    'infantry',
    200,
    16,
    3,
    'Construction, repair and mines',
    {
      members: 4,
      damage: 9,
      abilities: [
        'Construct',
        'Fighting position',
        'Barbed wire',
        'Repair',
        'Mine',
        'Clear mines',
        'Demolish obstacles',
      ],
    },
  ),
  eq(
    'eq_royal_grenadiers',
    'Royal Grenadier Section',
    'eq_training_center',
    'infantry',
    360,
    28,
    5,
    'Elite close assault',
    { members: 5, damage: 28, abilities: ['Assault', 'Concentrated grenade'] },
  ),
  eq(
    'eq_unicorn_support',
    'Unicorn Support Section',
    'eq_training_center',
    'infantry',
    340,
    26,
    5,
    'Shield and disrupt allies',
    { members: 4, abilities: ['Aegis', 'Distortion field'] },
  ),
  eq(
    'eq_lancer_at',
    'Lancer Anti-Tank Section',
    'eq_training_center',
    'infantry',
    330,
    25,
    4,
    'Guided anti-vehicle',
    { members: 4, range: 310, damage: 75, abilities: ['Focused shot'] },
  ),
  eq(
    'eq_forward_observer',
    'Forward Observer Team',
    'eq_training_center',
    'infantry',
    280,
    22,
    3,
    'Reconnaissance and artillery spotting',
    { members: 2, damage: 7, range: 130, abilities: ['Designate target'] },
  ),
  eq(
    'eq_pegasus_assault',
    'Pegasus Assault Flight',
    'eq_field_aerodrome',
    'air',
    380,
    30,
    6,
    'Airborne assault infantry',
    { members: 5, air: true, health: 250, abilities: ['Land / take off', 'Diving assault'] },
  ),
  eq(
    'eq_weather_team',
    'Weather Team',
    'eq_field_aerodrome',
    'air',
    320,
    28,
    5,
    'Airborne weather support',
    {
      members: 3,
      air: true,
      health: 180,
      damage: 4,
      abilities: ['Cloud screen', 'Clear skies', 'Headwind'],
    },
  ),
  eq(
    'eq_ah33_condor',
    'AH-33 Condor',
    'eq_field_aerodrome',
    'air',
    700,
    55,
    10,
    'Attack helicopter',
    {
      air: true,
      health: 530,
      damage: 35,
      range: 250,
      armor: 25,
      abilities: ['Rocket salvo', 'Evasive break'],
    },
  ),
  eq(
    'eq_th28m1',
    'TH-28M1',
    'eq_field_aerodrome',
    'air',
    520,
    45,
    8,
    'Heavy transport helicopter',
    { air: true, health: 560, damage: 12, abilities: ['Embark', 'Disembark', 'Rapid landing'] },
  ),
  eq(
    'eq_rv32a1_thermometer',
    'RV-32A1 Thermometer',
    'eq_mechanized_hq',
    'vehicle',
    260,
    20,
    4,
    'Fast reconnaissance',
    { health: 360, speed: 130, abilities: ['Reconnaissance pulse', 'Observation mode'] },
  ),
  eq(
    'eq_wc30',
    'WC-30',
    'eq_mechanized_hq',
    'vehicle',
    320,
    24,
    5,
    'APC and forward reinforcement',
    { health: 620, armor: 35, abilities: ['Deploy reinforcement point'] },
  ),
  eq(
    'eq_tc35_stalwart',
    'TC-35 Stalwart IFV',
    'eq_mechanized_hq',
    'vehicle',
    560,
    40,
    8,
    'Combined arms IFV',
    { health: 760, armor: 55, damage: 34, abilities: ['ATGM'] },
  ),
  eq(
    'eq_atv50_spatgm',
    'ATV-50 SPATGM',
    'eq_mechanized_hq',
    'vehicle',
    520,
    38,
    7,
    'Long range anti-tank',
    {
      health: 500,
      range: 360,
      damage: 95,
      abilities: ['Deploy stabilizers', 'Focused missile', 'Hold fire'],
    },
  ),
  eq(
    'eq_wc30ev_savior',
    'WC-30EV Savior',
    'eq_mechanized_hq',
    'vehicle',
    360,
    28,
    5,
    'Medical reinforcement vehicle',
    { health: 580, damage: 0, abilities: ['Heal', 'Reinforce'] },
  ),
  eq(
    'eq_lt32_peregrine',
    'LT-32 Peregrine',
    'eq_armored_hq',
    'vehicle',
    620,
    42,
    9,
    'Mobile anti-armor tank',
    {
      health: 730,
      armor: 55,
      damage: 95,
      range: 285,
      abilities: ['Rapid relocation', 'Prepared ambush'],
    },
  ),
  eq(
    'eq_t21a_sledgehammer',
    'T-21A Sledgehammer',
    'eq_armored_hq',
    'vehicle',
    980,
    70,
    14,
    'Heavy main battle tank',
    {
      health: 1150,
      armor: 130,
      damage: 145,
      range: 280,
      speed: 62,
      abilities: ['AP / HE ammunition'],
    },
  ),
  eq(
    'eq_sh32',
    '150 mm SH-32',
    'eq_armored_hq',
    'vehicle',
    760,
    58,
    11,
    'Self-propelled artillery',
    {
      health: 620,
      armor: 25,
      range: 500,
      damage: 120,
      abilities: ['Barrage', 'Smoke barrage', 'Precision barrage'],
    },
  ),
  eq('eq_t21ahq', 'T-21AHQ', 'eq_armored_hq', 'vehicle', 820, 60, 12, 'Command and sensor tank', {
    health: 820,
    armor: 70,
    damage: 24,
    abilities: ['Command aura'],
  }),
  idf(
    'id_coalition_rifle',
    'Coalition Rifle Squad',
    'id_forward_command_post',
    'infantry',
    250,
    18,
    4,
    'Capture and line infantry',
    { members: 6, abilities: ['Grenade', 'Smoke grenade', 'LMG package', 'AT package'] },
  ),
  idf(
    'id_combat_engineers',
    'Combat Engineer Squad',
    'id_forward_command_post',
    'infantry',
    210,
    16,
    3,
    'Construction, repair and mines',
    {
      members: 4,
      damage: 9,
      abilities: [
        'Construct',
        'Fighting position',
        'Barbed wire',
        'Repair',
        'Mine',
        'Clear mines',
        'Demolish obstacles',
      ],
    },
  ),
  idf(
    'id_m2hb',
    'M2HB Heavy Machine Gun Team',
    'id_forward_command_post',
    'infantry',
    300,
    24,
    4,
    'Suppression and anti-air',
    { members: 4, range: 270, damage: 13, antiAir: true, abilities: ['Set up'] },
  ),
  idf(
    'id_fgm148',
    'FGM-148 Javelin Team',
    'id_forward_command_post',
    'infantry',
    350,
    27,
    4,
    'Long range anti-tank',
    { members: 3, range: 350, damage: 105, abilities: ['Top attack'] },
  ),
  idf(
    'id_fim92',
    'FIM-92 Stinger Team',
    'id_forward_command_post',
    'infantry',
    330,
    26,
    4,
    'Concealable anti-air',
    { members: 3, range: 330, damage: 65, antiAir: true, abilities: ['Lock missile'] },
  ),
  idf(
    'id_goc_intervention',
    'GOC Intervention Section',
    'id_forward_command_post',
    'infantry',
    440,
    34,
    6,
    'Elite counter-support infantry',
    { members: 5, damage: 30, abilities: ['Countermeasure pulse'] },
  ),
  idf(
    'id_joint_recon',
    'Joint Reconnaissance Team',
    'id_forward_command_post',
    'infantry',
    300,
    22,
    3,
    'Camouflaged reconnaissance',
    { members: 2, damage: 7, range: 140, abilities: ['Laser designate', 'Observation drone'] },
  ),
  idf(
    'id_rq7b_shadow',
    'RQ-7B Shadow',
    'id_army_aviation',
    'drone',
    220,
    20,
    3,
    'Unarmed recon drone',
    {
      air: true,
      health: 150,
      damage: 0,
      speed: 170,
      abilities: ['Observation orbit', 'Target designation'],
    },
  ),
  idf(
    'id_ah64e',
    'AH-64E Apache Guardian',
    'id_army_aviation',
    'air',
    760,
    58,
    11,
    'Dedicated anti-armor helicopter',
    {
      air: true,
      health: 540,
      damage: 82,
      range: 330,
      armor: 25,
      abilities: ['Hellfire volley', 'Mast sensor'],
    },
  ),
  idf(
    'id_uh60m',
    'UH-60M Black Hawk',
    'id_army_aviation',
    'air',
    500,
    42,
    8,
    'Transport helicopter',
    {
      air: true,
      health: 540,
      damage: 12,
      abilities: ['Embark', 'Disembark', 'Casualty evacuation'],
    },
  ),
  idf('id_fennek', 'Fennek', 'id_mechanized_ops', 'vehicle', 280, 21, 4, 'Sensor reconnaissance', {
    health: 370,
    speed: 132,
    abilities: ['Sensor mast', 'Active scan'],
  }),
  idf(
    'id_m113a3',
    'M113A3',
    'id_mechanized_ops',
    'vehicle',
    330,
    25,
    5,
    'APC and forward reinforcement',
    { health: 630, armor: 35, abilities: ['Deploy reinforcement point'] },
  ),
  idf(
    'id_m2a4_bradley',
    'M2A4 Bradley',
    'id_mechanized_ops',
    'vehicle',
    600,
    43,
    8,
    'Combined arms IFV',
    { health: 780, armor: 60, damage: 38, abilities: ['TOW missile'] },
  ),
  idf(
    'id_m1134',
    'M1134 Stryker ATGM',
    'id_mechanized_ops',
    'vehicle',
    540,
    39,
    7,
    'Fast anti-tank vehicle',
    {
      health: 520,
      range: 360,
      damage: 98,
      abilities: ['Observation position', 'Focused TOW', 'Hold fire'],
    },
  ),
  idf(
    'id_m1133',
    'M1133 Medical Evacuation Vehicle',
    'id_mechanized_ops',
    'vehicle',
    370,
    28,
    5,
    'Mobile medical reinforcement',
    { health: 590, damage: 0, abilities: ['Deploy medical', 'Heal', 'Reinforce'] },
  ),
  idf(
    'id_mshorad',
    'M-SHORAD',
    'id_mechanized_ops',
    'vehicle',
    560,
    40,
    7,
    'Mobile short range air defense',
    { health: 650, armor: 40, damage: 44, antiAir: true, abilities: ['Active radar'] },
  ),
  idf(
    'id_m1128',
    'M1128 Mobile Gun System',
    'id_armored_ops',
    'vehicle',
    650,
    44,
    9,
    'Wheeled direct fire vehicle',
    { health: 710, armor: 50, damage: 100, abilities: ['Canister shot'] },
  ),
  idf(
    'id_m1a2_sep_v3',
    'M1A2 SEP v3 Abrams',
    'id_armored_ops',
    'vehicle',
    1050,
    75,
    15,
    'Heavy main battle tank',
    {
      health: 1200,
      armor: 145,
      damage: 130,
      range: 290,
      speed: 60,
      abilities: ['Smoke launchers', 'Advanced sabot'],
    },
  ),
  idf(
    'id_m109a7',
    'M109A7 Paladin',
    'id_armored_ops',
    'vehicle',
    780,
    60,
    11,
    '155 mm self-propelled artillery',
    {
      health: 640,
      armor: 25,
      range: 500,
      damage: 125,
      abilities: ['Barrage', 'Smoke barrage', 'Precision mission'],
    },
  ),
  idf(
    'id_m4_command',
    'M4 Command and Control Vehicle',
    'id_armored_ops',
    'vehicle',
    800,
    60,
    12,
    'Command and sensor vehicle',
    { health: 820, armor: 65, damage: 18, abilities: ['Command aura'] },
  ),
  idf(
    'id_m88a2',
    'M88A2 Hercules',
    'id_armored_ops',
    'vehicle',
    500,
    40,
    7,
    'Recovery and repair vehicle',
    { health: 800, armor: 55, damage: 0, abilities: ['Repair', 'Recover wreck', 'Salvage'] },
  ),
];

export const buildings: BuildingDefinition[] = [
  {
    id: 'eq_training_center',
    faction: 'equestria',
    name: 'Training Center',
    cost: 250,
    buildTime: 30,
    prerequisites: ['eq_hq'],
    unlocks: units.filter((u) => u.source === 'eq_training_center').map((u) => u.id),
  },
  {
    id: 'eq_field_aerodrome',
    faction: 'equestria',
    name: 'Field Aerodrome',
    cost: 400,
    buildTime: 45,
    prerequisites: ['eq_training_center'],
    unlocks: units.filter((u) => u.source === 'eq_field_aerodrome').map((u) => u.id),
  },
  {
    id: 'eq_mechanized_hq',
    faction: 'equestria',
    name: 'Mechanized Headquarters',
    cost: 425,
    buildTime: 45,
    prerequisites: ['eq_training_center'],
    unlocks: units.filter((u) => u.source === 'eq_mechanized_hq').map((u) => u.id),
  },
  {
    id: 'eq_armored_hq',
    faction: 'equestria',
    name: 'Armored Headquarters',
    cost: 600,
    buildTime: 60,
    prerequisites: ['eq_mechanized_hq'],
    unlocks: units.filter((u) => u.source === 'eq_armored_hq').map((u) => u.id),
  },
  {
    id: 'eq_air_coordination',
    faction: 'equestria',
    name: 'Air Coordination Center',
    cost: 500,
    buildTime: 50,
    prerequisites: ['eq_field_aerodrome'],
    unlocks: [],
  },
  {
    id: 'id_forward_command_post',
    faction: 'interdef',
    name: 'Forward Command Post',
    cost: 250,
    buildTime: 30,
    prerequisites: ['id_hq'],
    unlocks: units.filter((u) => u.source === 'id_forward_command_post').map((u) => u.id),
  },
  {
    id: 'id_army_aviation',
    faction: 'interdef',
    name: 'Army Aviation Command',
    cost: 400,
    buildTime: 45,
    prerequisites: ['id_forward_command_post'],
    unlocks: units.filter((u) => u.source === 'id_army_aviation').map((u) => u.id),
  },
  {
    id: 'id_mechanized_ops',
    faction: 'interdef',
    name: 'Mechanized Operations Center',
    cost: 425,
    buildTime: 45,
    prerequisites: ['id_forward_command_post'],
    unlocks: units.filter((u) => u.source === 'id_mechanized_ops').map((u) => u.id),
  },
  {
    id: 'id_armored_ops',
    faction: 'interdef',
    name: 'Armored Operations Center',
    cost: 600,
    buildTime: 60,
    prerequisites: ['id_mechanized_ops'],
    unlocks: units.filter((u) => u.source === 'id_armored_ops').map((u) => u.id),
  },
  {
    id: 'id_joint_fires',
    faction: 'interdef',
    name: 'Joint Fires Coordination Center',
    cost: 500,
    buildTime: 50,
    prerequisites: ['id_army_aviation'],
    unlocks: [],
  },
];

export interface AbilityDefinition {
  id: string;
  name: string;
  source: string;
  faction: Faction;
  supplyCost: number;
  cooldown: number;
  kind: 'smoke' | 'damage' | 'recon' | 'shield' | 'land' | 'deploy' | 'heal' | 'scan' | 'headwind';
  radius: number;
  damage?: number | undefined;
  delay?: number | undefined;
  /** Minimum range for artillery barrages; omitted for non-artillery */
  minimumRange?: number | undefined;
  /** Whether this ability requires a directional input (e.g., cannon run, A-10 run) */
  directional?: boolean | undefined;
  /** Whether this ability telegraphs before activation (airstrike warning) */
  telegraphed?: boolean | undefined;
  /** Guided-weapon lock time in seconds; being inside smoke/EW cancels the lock */
  lockTime?: number | undefined;
}

const support = (
  faction: Faction,
  source: string,
  id: string,
  name: string,
  supplyCost: number,
  cooldown: number,
  kind: AbilityDefinition['kind'],
  radius: number,
  damage?: number,
  delay?: number,
  options: Partial<
    Pick<AbilityDefinition, 'minimumRange' | 'directional' | 'telegraphed' | 'lockTime'>
  > = {},
): AbilityDefinition => ({
  id,
  name,
  source,
  faction,
  supplyCost,
  cooldown,
  kind,
  radius,
  damage,
  delay,
  ...options,
});
export const abilities: AbilityDefinition[] = [
  support(
    'equestria',
    'eq_air_coordination',
    'eq_recon_sweep',
    'Pegasus Reconnaissance Sweep',
    80,
    45,
    'recon',
    230,
  ),
  support(
    'equestria',
    'eq_air_coordination',
    'eq_cloud_screen',
    'Cloud Screen',
    120,
    60,
    'smoke',
    170,
  ),
  support(
    'equestria',
    'eq_air_coordination',
    'eq_cannon_run',
    'Tactical Cannon Run',
    190,
    100,
    'damage',
    120,
    90,
    2,
    { directional: true, telegraphed: true },
  ),
  support(
    'equestria',
    'eq_air_coordination',
    'eq_precision_bombing',
    'Precision Bombing Run',
    300,
    150,
    'damage',
    95,
    220,
    3,
    { telegraphed: true },
  ),
  support(
    'equestria',
    'eq_air_coordination',
    'eq_weather_front',
    'Localized Weather Front',
    220,
    120,
    'smoke',
    250,
  ),
  support(
    'interdef',
    'id_joint_fires',
    'id_rq4_pass',
    'RQ-4 Reconnaissance Pass',
    80,
    45,
    'recon',
    280,
  ),
  support(
    'interdef',
    'id_joint_fires',
    'id_smoke_mission',
    '155 mm Smoke Mission',
    100,
    55,
    'smoke',
    180,
  ),
  support(
    'interdef',
    'id_joint_fires',
    'id_a10_run',
    'A-10C Gun Run',
    200,
    100,
    'damage',
    120,
    105,
    2,
    { directional: true, telegraphed: true },
  ),
  support(
    'interdef',
    'id_joint_fires',
    'id_f16_strike',
    'F-16C Precision Strike',
    310,
    150,
    'damage',
    95,
    235,
    3,
    { telegraphed: true },
  ),
  support(
    'interdef',
    'id_joint_fires',
    'id_electronic_attack',
    'Electronic Attack Window',
    180,
    120,
    'smoke',
    220,
  ),
];

export const getAbility = (id: string): AbilityDefinition | undefined =>
  abilities.find((entry) => entry.id === id);

export const sectors: SectorDefinition[] = [
  {
    id: 'id_base',
    name: 'Western INTERDEF Base',
    type: 'base',
    point: { x: 260, y: 760 },
    neighbors: ['north_supply', 'south_supply', 'west_supply'],
    initialOwner: 'interdef',
  },
  {
    id: 'west_supply',
    name: 'Western Motor Pool Depot',
    type: 'supply',
    point: { x: 700, y: 1030 },
    neighbors: ['id_base', 'south_supply'],
  },
  {
    id: 'north_supply',
    name: 'Northwest Depot',
    type: 'supply',
    point: { x: 680, y: 360 },
    neighbors: ['id_base', 'north_vp', 'central_supply'],
  },
  {
    id: 'north_vp',
    name: 'Rail Yard Victory Point',
    type: 'victory',
    point: { x: 1410, y: 310 },
    neighbors: ['north_supply', 'central_supply', 'central_vp'],
  },
  {
    id: 'central_supply',
    name: 'Warehouse Depot',
    type: 'supply',
    point: { x: 1210, y: 750 },
    neighbors: ['north_supply', 'north_vp', 'central_vp', 'south_supply'],
  },
  {
    id: 'central_vp',
    name: 'Industrial Junction',
    type: 'victory',
    point: { x: 2020, y: 760 },
    neighbors: ['north_vp', 'central_supply', 'east_supply', 'south_vp'],
  },
  {
    id: 'south_supply',
    name: 'Residential Depot',
    type: 'supply',
    point: { x: 970, y: 1280 },
    neighbors: ['id_base', 'west_supply', 'central_supply', 'south_vp'],
  },
  {
    id: 'south_vp',
    name: 'Residential Square',
    type: 'victory',
    point: { x: 1880, y: 1350 },
    neighbors: ['south_supply', 'central_vp', 'east_supply'],
  },
  {
    id: 'east_supply',
    name: 'Eastern Depot',
    type: 'supply',
    point: { x: 2700, y: 970 },
    neighbors: ['central_vp', 'south_vp', 'north_east_supply', 'south_east_supply', 'eq_base'],
  },
  {
    id: 'north_east_supply',
    name: 'Rail Siding Depot',
    type: 'supply',
    point: { x: 2860, y: 420 },
    neighbors: ['east_supply', 'eq_base'],
  },
  {
    id: 'south_east_supply',
    name: 'Southern Works Depot',
    type: 'supply',
    point: { x: 2860, y: 1300 },
    neighbors: ['east_supply', 'eq_base'],
  },
  {
    id: 'eq_base',
    name: 'Eastern Equestrian Base',
    type: 'base',
    point: { x: 3620, y: 760 },
    neighbors: ['east_supply', 'north_east_supply', 'south_east_supply'],
    initialOwner: 'equestria',
  },
];

export const getUnit = (id: string): UnitDefinition | undefined =>
  units.find((entry) => entry.id === id);
export const getBuilding = (id: string): BuildingDefinition | undefined =>
  buildings.find((entry) => entry.id === id);
